import {
  Button,
  Card,
  CardHeader,
  CardFooter,
  Table,
  Container,
  Row,
  Modal,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Input,
  Label,
  UncontrolledDropdown,
  DropdownMenu,
  DropdownItem,
  DropdownToggle,
  Pagination,
  PaginationItem,
  PaginationLink,
} from "reactstrap";
import { useEffect, useState, useMemo } from "react";
import Header from "components/Headers/Header.js";
import axios from "axios";
import Loader from "components/Loader";

const API_URL = process.env.REACT_APP_API_URL;

const TableGroups = () => {
  const [groups, setGroups] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newGroup, setNewGroup] = useState({ wa_id: "", name: "", countryId: "" });
  const [modalOpen, setModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [currentGroup, setCurrentGroup] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [groupsPerPage] = useState(10);
  const [sortOrder, setSortOrder] = useState("asc");

  useEffect(() => {
    const fetchAllData = async () => {
      try {
        const [groupsRes, countriesRes] = await Promise.all([
          axios.get(`${API_URL}/groups`),
          axios.get(`${API_URL}/countries`),
        ]);
        setGroups(groupsRes.data);
        setCountries(countriesRes.data);
      } catch (error) {
        console.error("Erro ao buscar dados:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, []);

  const sortedGroups = useMemo(() => {
    return [...groups].sort((a, b) => {
      const nameA = a.Country?.name?.toLowerCase() || "";
      const nameB = b.Country?.name?.toLowerCase() || "";
      return sortOrder === "asc" ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
    });
  }, [groups, sortOrder]);

  if (loading) {
    return (
      <>
        <Header />
        <Loader />
      </>
    );
  }

  const totalPages = Math.ceil(sortedGroups.length / groupsPerPage);
  const indexOfLastGroup = currentPage * groupsPerPage;
  const indexOfFirstGroup = indexOfLastGroup - groupsPerPage;
  const currentGroups = sortedGroups.slice(indexOfFirstGroup, indexOfLastGroup);

  const toggleModal = () => setModalOpen(!modalOpen);
  const toggleEditModal = () => setEditModalOpen(!editModalOpen);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewGroup({ ...newGroup, [name]: value });
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentGroup({ ...currentGroup, [name]: value });
  };

  const handleCreateGroup = async () => {
    try {
      const response = await axios.post(`${API_URL}/groups`, newGroup);
      setGroups([...groups, response.data.group]);
      setNewGroup({ wa_id: "", name: "", countryId: "" });
      toggleModal();
    } catch (error) {
      console.error("Erro ao criar grupo:", error);
    }
  };

  const handleEditGroup = async () => {
    try {
      const response = await axios.put(`${API_URL}/groups/${currentGroup.id}`, currentGroup);
      setGroups(groups.map(g => g.id === currentGroup.id ? response.data.group : g));
      toggleEditModal();
    } catch (error) {
      console.error("Erro ao editar grupo:", error);
    }
  };

  const handleDeleteGroup = async (id) => {
    try {
      await axios.delete(`${API_URL}/groups/${id}`);
      setGroups(groups.filter((g) => g.id !== id));
    } catch (error) {
      console.error("Erro ao deletar grupo:", error);
    }
  };

  const openEditModal = (group) => {
    setCurrentGroup(group);
    toggleEditModal();
  };

  const handlePageChange = (e, pageNumber) => {
    e.preventDefault();
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  return (
    <>
      <Header />
      <Container className="mt--7" fluid>
        <Row>
          <div className="col">
            <Card className="shadow">
              <CardHeader className="border-0 d-flex justify-content-between align-items-center">
                <h3 className="mb-0">Lista de Grupos</h3>
                <Button color="warning" onClick={toggleModal}>
                  Novo Grupo
                </Button>
              </CardHeader>
              <Table className="align-items-center table-flush" responsive>
                <thead className="thead-light">
                  <tr>
                    <th scope="col" style={{ cursor: "pointer" }} onClick={() => setSortOrder(sortOrder === "asc" ? "desc" : "asc")}>
                      País {sortOrder === "asc" ? "▲" : "▼"}
                    </th>
                    <th scope="col">Nome do Grupo</th>
                    <th scope="col">ID do Grupo (wa_id)</th>
                    <th scope="col" className="text-right" />
                  </tr>
                </thead>
                <tbody>
                  {currentGroups.map((group) => (
                    <tr key={group.id}>
                      <td>{group.Country?.name || "-"}</td>
                      <td>{group.name}</td>
                      <td>{group.wa_id}</td>
                      <td className="text-right">
                        <UncontrolledDropdown>
                          <DropdownToggle className="btn-icon-only text-light" href="#pablo" role="button" size="sm" color="" onClick={(e) => e.preventDefault()}>
                            <i className="fas fa-ellipsis-v" />
                          </DropdownToggle>
                          <DropdownMenu container="body" className="dropdown-menu-arrow" right>
                            <DropdownItem onClick={() => openEditModal(group)}>Editar</DropdownItem>
                            <DropdownItem onClick={() => handleDeleteGroup(group.id)}>Deletar</DropdownItem>
                          </DropdownMenu>
                        </UncontrolledDropdown>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              <CardFooter className="py-4">
                <Pagination className="pagination justify-content-end mb-0">
                  <PaginationItem disabled={currentPage === 1}>
                    <PaginationLink href="#prev" onClick={(e) => handlePageChange(e, currentPage - 1)}>
                      <i className="fas fa-angle-left" />
                    </PaginationLink>
                  </PaginationItem>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <PaginationItem key={page} active={page === currentPage}>
                      <PaginationLink href="#page" onClick={(e) => handlePageChange(e, page)}>
                        {page}
                      </PaginationLink>
                    </PaginationItem>
                  ))}
                  <PaginationItem disabled={currentPage === totalPages}>
                    <PaginationLink href="#next" onClick={(e) => handlePageChange(e, currentPage + 1)}>
                      <i className="fas fa-angle-right" />
                    </PaginationLink>
                  </PaginationItem>
                </Pagination>
              </CardFooter>
            </Card>
          </div>
        </Row>
      </Container>

      {/* Modais */}
      <Modal isOpen={modalOpen} toggle={toggleModal}>
        <div className="modal-header">
          <h5 className="modal-title">Criar Novo Grupo</h5>
          <button className="close" type="button" onClick={toggleModal}>
            <span aria-hidden={true}>×</span>
          </button>
        </div>
        <ModalBody>
          <Form>
            <FormGroup>
              <Label for="name">Nome do Grupo</Label>
              <Input type="text" id="name" name="name" value={newGroup.name} onChange={handleInputChange} placeholder="Ex: Grupo Jovens Brasil" />
            </FormGroup>
            <FormGroup>
              <Label for="wa_id">ID do Grupo</Label>
              <Input type="text" id="wa_id" name="wa_id" value={newGroup.wa_id} onChange={handleInputChange} placeholder="Ex: 120363012345678@g.us" />
            </FormGroup>
            <FormGroup>
              <Label for="countryId">País</Label>
              <Input type="select" id="countryId" name="countryId" value={newGroup.countryId} onChange={handleInputChange}>
                <option value="">Selecione</option>
                {countries.map((country) => (
                  <option key={country.id} value={country.id}>
                    {country.name}
                  </option>
                ))}
              </Input>
            </FormGroup>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={handleCreateGroup}>Criar Grupo</Button>
          <Button color="secondary" onClick={toggleModal}>Cancelar</Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={editModalOpen} toggle={toggleEditModal}>
        <div className="modal-header">
          <h5 className="modal-title">Editar Grupo</h5>
          <button className="close" type="button" onClick={toggleEditModal}>
            <span aria-hidden={true}>×</span>
          </button>
        </div>
        <ModalBody>
          <Form>
            <FormGroup>
              <Label for="name">Nome do Grupo</Label>
              <Input type="text" id="name" name="name" value={currentGroup?.name || ""} onChange={handleEditInputChange} />
            </FormGroup>
            <FormGroup>
              <Label for="wa_id">ID do Grupo</Label>
              <Input type="text" id="wa_id" name="wa_id" value={currentGroup?.wa_id || ""} onChange={handleEditInputChange} />
            </FormGroup>
            <FormGroup>
              <Label for="countryId">País</Label>
              <Input type="select" id="countryId" name="countryId" value={currentGroup?.countryId || ""} onChange={handleEditInputChange}>
                <option value="">Selecione</option>
                {countries.map((country) => (
                  <option key={country.id} value={country.id}>
                    {country.name}
                  </option>
                ))}
              </Input>
            </FormGroup>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={handleEditGroup}>Atualizar Grupo</Button>
          <Button color="secondary" onClick={toggleEditModal}>Cancelar</Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default TableGroups;